
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TrendingUp, ArrowRightLeft, ChevronDown, Search, ArrowRight, Loader2, Menu, Clock, Newspaper, Zap, User, ExternalLink, ChevronRight, Sparkles, List, Plus, Crown, MessageSquare } from 'lucide-react';
import { XlevenLogo } from '../Common/XlevenLogo';
import { Skeleton } from '../Common/Skeleton';
import { StandingsModal } from '../Games/StandingsModal';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { getLatestTransfers, getTopTransferGroups, getTransfersByGroupId, fetchMajorGamesToday, fetchSupabaseNewsList, fetchUserLeagueSelectionsFromDB } from '../../services/footballService'; 
import { getLeagueName } from '../../services/leagueService';
import { getFromCache, saveToCache } from '../../services/storageService';
import { TransferNews, TransferGroup, Game, NewsItem } from '../../types';
import { t } from '../../i18n';
import { fetchActiveBanner, Banner } from '../../services/bannerService';
const TransferCard = React.lazy(() => import('../Home/TransferCard').then(m => ({ default: m.TransferCard })));
const SearchOverlay = React.lazy(() => import('../Home/SearchOverlay').then(m => ({ default: m.SearchOverlay })));
const LatestMovements = React.lazy(() => import('../Home/LatestMovements').then(m => ({ default: m.LatestMovements })));
const HomeNewsCard = React.lazy(() => import('../Home/HomeNewsCard').then(m => ({ default: m.HomeNewsCard })));
const HighlightSection = React.lazy(() => import('../Home/HighlightSection').then(m => ({ default: m.HighlightSection })));
const MagazineSection = React.lazy(() => import('../Home/MagazineSection').then(m => ({ default: m.MagazineSection })));
const WebtoonSection = React.lazy(() => import('../Home/WebtoonSection').then(m => ({ default: m.WebtoonSection })));
const TeamRankingSection = React.lazy(() => import('../Home/TeamRankingSection').then(m => ({ default: m.TeamRankingSection })));
const MatchAnalysisSection = React.lazy(() => import('../Home/MatchAnalysisSection').then(m => ({ default: m.MatchAnalysisSection })));
import SEO from '../Common/SEO';
import { WeeklyMagazineLogo } from '../Common/WeeklyMagazineLogo';
import { AppFooter } from '../Common/AppFooter';

interface HomeViewProps {
  onNavigateToPlayer: (id: string) => void;
  onOpenSidebar: () => void;
}

export const HomeView: React.FC<HomeViewProps> = ({ onNavigateToPlayer, onOpenSidebar }) => {
  const { lang } = useLanguage();
  const { user, isLoading: isAuthLoading } = useAuth();

  const seoTitle = t(lang, 'seoHomeTitle');
  const seoDescription = t(lang, 'seoDefaultDescription');
  const [loading, setLoading] = useState(!getFromCache('home_major_games'));
  
  const newsScrollRef = useRef<HTMLDivElement>(null);
  const transferNewsScrollRef = useRef<HTMLDivElement>(null);
  const gamesScrollRef = useRef<HTMLDivElement>(null);
  const [trendingPlayers, setTrendingPlayers] = useState<TransferNews[]>([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [majorGames, setMajorGames] = useState<Game[]>([]);
  const [allNews, setAllNews] = useState<NewsItem[]>([]);
  const [magazineNews, setMagazineNews] = useState<NewsItem[]>([]);
  const [webtoonNews, setWebtoonNews] = useState<NewsItem[]>([]);
  const [hasUserLeagues, setHasUserLeagues] = useState(true);

  const [isDragging, setIsDragging] = useState(false);
  const [isWorldCupModalOpen, setIsWorldCupModalOpen] = useState(false);
  const [wcBanner, setWcBanner] = useState<Banner | null>(null);
  const [wcBannerLoaded, setWcBannerLoaded] = useState(false);

  // Memoized scroll handlers to prevent recreation on every render
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(false);
    const container = e.currentTarget;
    let isDown = true;
    let startX = e.pageX - container.offsetLeft;
    let scrollLeft = container.scrollLeft;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      setIsDragging(true);
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      isDown = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      e.stopPropagation();
      setIsDragging(false);
    }
  }, [isDragging]);

  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
        if (isAuthLoading) return;
        
        const cachedGames = getFromCache<Game[]>('home_major_games');
        const cachedTrending = getFromCache<TransferNews[]>('home_trending_players');
        const cachedNews = getFromCache<NewsItem[]>('home_all_news');

        if (cachedGames) setMajorGames(cachedGames);
        if (cachedTrending) setTrendingPlayers(cachedTrending);
        if (cachedNews) setAllNews(cachedNews);

        if (!cachedGames || !cachedTrending || !cachedNews) {
            setLoading(true);
        }

        try {
            let userLeagues: string[] | undefined;
            if (user) {
                const dbSelections = await fetchUserLeagueSelectionsFromDB(user.id);
                userLeagues = dbSelections.map(s => s.league_id);
                const hasLeagues = !!(userLeagues && userLeagues.length > 0);

                if (isMounted) setHasUserLeagues(hasLeagues);
            } else {
                if (isMounted) setHasUserLeagues(true); 
            }

            // critical data (above the fold)
            const [latest, games, newsData] = await Promise.all([
                getLatestTransfers().catch(() => []),
                fetchMajorGamesToday(userLeagues).catch(() => []),
                fetchSupabaseNewsList(lang, 0, 50).catch(() => ({ news: [] }))
            ]);

            if (isMounted) {
                // 최근 이적은 transferdate 최신순으로 정렬 (날짜가 같으면 ID 내림차순 정렬)
                const sortedLatest = (latest || []).sort((a, b) => {
                    const parseDate = (d: string | undefined) => {
                        if (!d) return 0;
                        if (d.includes('/')) {
                            const [day, month, year] = d.split('/').map(Number);
                            return new Date(year, month - 1, day).getTime();
                        }
                        return new Date(d).getTime();
                    };

                    const dateA = parseDate(a.date);
                    const dateB = parseDate(b.date);
                    
                    if (dateB !== dateA) {
                        return dateB - dateA;
                    }
                    
                    const idA = parseInt(a.id) || 0;
                    const idB = parseInt(b.id) || 0;
                    return idB - idA;
                });
                setTrendingPlayers(sortedLatest);
                setMajorGames(games || []);
                setAllNews(newsData.news || []);
                setLoading(false); // First meaningful paint done

                saveToCache('home_major_games', games, 1000 * 60 * 5); // 5 min
                saveToCache('home_trending_players', sortedLatest, 1000 * 60 * 10); // 10 min
                saveToCache('home_all_news', newsData.news, 1000 * 60 * 5); // 5 min
            }

            // defer less critical data
            setTimeout(async () => {
              const [magazineData, webtoonData] = await Promise.all([
                  fetchSupabaseNewsList(lang, 0, 5, undefined, 'magazine').catch(() => ({ news: [] })),
                  fetchSupabaseNewsList(lang, 0, 5, undefined, 'webtoon').catch(() => ({ news: [] }))
              ]);

              if (isMounted) {
                  setMagazineNews((magazineData.news || []).slice(0, 5));
                  setWebtoonNews((webtoonData.news || []).slice(0, 5));
              }
            }, 100);

            // Restore scrolls
            requestAnimationFrame(() => {
                const homeScroll = sessionStorage.getItem('home_scroll_y');
                if (homeScroll) {
                    window.scrollTo(0, parseInt(homeScroll));
                }

                const newsScroll = sessionStorage.getItem('home_news_scroll');
                if (newsScroll && newsScrollRef.current) newsScrollRef.current.scrollLeft = parseInt(newsScroll);

                const transferScroll = sessionStorage.getItem('home_transfer_scroll');
                if (transferScroll && transferNewsScrollRef.current) transferNewsScrollRef.current.scrollLeft = parseInt(transferScroll);

                const gamesScroll = sessionStorage.getItem('home_games_scroll');
                if (gamesScroll && gamesScrollRef.current) gamesScrollRef.current.scrollLeft = parseInt(gamesScroll);
            });
        } catch (e) { 
            console.error("HomeView initialization failed", e); 
            if (isMounted) setLoading(false);
        }
    };

    init();
    
    const handleScroll = () => {
        sessionStorage.setItem('home_scroll_y', window.scrollY.toString());
    };
    window.addEventListener('scroll', handleScroll);

    return () => { 
        isMounted = false; 
        window.removeEventListener('scroll', handleScroll);
        if (newsScrollRef.current) sessionStorage.setItem('home_news_scroll', newsScrollRef.current.scrollLeft.toString());
        if (transferNewsScrollRef.current) sessionStorage.setItem('home_transfer_scroll', transferNewsScrollRef.current.scrollLeft.toString());
        if (gamesScrollRef.current) sessionStorage.setItem('home_games_scroll', gamesScrollRef.current.scrollLeft.toString());
    };
  }, [lang, user?.id, isAuthLoading]);

  useEffect(() => {
    fetchActiveBanner('main', 'worldcup')
      .then(b => { setWcBanner(b); setWcBannerLoaded(true); })
      .catch(() => setWcBannerLoaded(true));
  }, []);

  const pickedNews = useMemo(() => {
    return allNews
      .filter(n => {
        const type = n.type?.toLowerCase();
        return (
          type !== 'transfer' &&
          type !== 'gossip' &&
          type !== 'webtoon' &&
          type !== 'magazine' &&
          (n.source || n.user_id)
        );
      })
      .slice(0, 5);
  }, [allNews]);

  const transferNews = useMemo(() => {
    return allNews.filter(n => (n.type === 'transfer' || n.type === 'market') && (n.source || n.user_id)).slice(0, 5);
  }, [allNews]);

  const magazineNewsRef = useRef<HTMLDivElement>(null);

  const renderNewsCard = (item: NewsItem, type: 'general' | 'transfer') => {
      const sourceLower = item.source?.toLowerCase() || '';
      const isPartnerSource = sourceLower.includes('bbc') || sourceLower.includes('teamtalk') || sourceLower.includes('si') || sourceLower.includes('sports illustrated');
      const isWeeklyMagazine = sourceLower.includes('magazine');
      const isUserPost = !!item.user_id;
      const displayName = isPartnerSource ? 'XLEVEN' : (isUserPost ? item.author?.name : (item.source || null));
      if (!displayName && !isUserPost) return null;

      const avatarUrl = isPartnerSource ? null : (isUserPost ? item.author?.avatar : (item.sourceIcon || null));
      const displayImage = item.image;
        
      if (displayImage) {
          return (
            <div 
                key={item.id} 
                className="flex-shrink-0 w-[300px] group bg-slate-900 rounded-[28px] overflow-hidden cursor-pointer shadow-lg transition-all snap-center relative border border-white/5"
            >
                <div className="aspect-video relative overflow-hidden">
                    <img src={displayImage} alt="" className="w-full h-full object-cover transition-transform duration-[2000ms] md:group-hover:scale-110 opacity-70" />
                    <div 
                        onClick={() => window.navigateTo(`/feed/item/${item.id}`)} 
                        className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent"
                    ></div>
                    <div className="absolute top-3 left-3 flex items-center gap-2 z-1">
                        <div className="w-7 h-7 rounded-full overflow-hidden border border-white/20 bg-slate-800 flex items-center justify-center shadow-md">
                            {isPartnerSource ? <XlevenLogo theme="dark" hideText={true} /> : avatarUrl ? <img src={avatarUrl} className="w-full h-full object-cover" alt="" /> : isWeeklyMagazine ? <WeeklyMagazineLogo size={24}/> : <User size={12} className="text-white/40" />}
                        </div>
                        <span className="text-[9px] font-black text-white uppercase tracking-widest drop-shadow-md">{displayName}</span>
                    </div>
                    <div className="absolute top-3 right-3 z-20">
                        <a href={item.url} target='_blank'>
                            <div className="px-2 py-1 bg-rose-500 backdrop-blur-md rounded-lg text-[10px] font-black text-white uppercase flex items-center gap-1 border border-white/10 shadow-lg">{item.source === 'magazine' || item.source === 'webtoon' ? 'openai' : item.source || 'INTEL'}<ExternalLink size={8} strokeWidth={3} /></div>
                        </a>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                        <div className="flex items-center gap-2 mb-1.5"><div className={`px-1.5 py-0.5 rounded text-[7px] font-black text-white uppercase tracking-widest ${type === 'transfer' ? 'bg-rose-500' : 'bg-indigo-500'}`}>{type === 'transfer' ? 'Transfer' : (item.type || 'General')}</div><span className="text-[8px] font-bold text-white/50 uppercase tracking-tighter">{item.time}</span></div>
                        <h3 className="text-sm md:text-base font-black text-white uppercase tracking-tight leading-tight line-clamp-2 drop-shadow-lg pr-12">{item.title}</h3>
                    </div>
                </div>
            </div>
          );
      }
      return (
        <div key={item.id} onClick={() => window.navigateTo(`/feed/item/${item.id}`)} className="flex-shrink-0 w-[260px] md:w-[300px] group bg-white rounded-[28px] border border-gray-100 p-6 flex flex-col justify-between cursor-pointer shadow-sm md:hover:shadow-md md:hover:border-rose-200 transition-all snap-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-[0.03] pointer-events-none"><Newspaper size={80} strokeWidth={1} /></div>
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">{isPartnerSource ? <XlevenLogo theme="light" hideText={true} /> : avatarUrl ? <img src={avatarUrl} className="w-full h-full object-cover" alt="" /> : <User size={14} className="text-slate-300" />}</div>
                    <div className="flex flex-col min-w-0"><span className="text-[10px] font-black text-slate-900 uppercase tracking-tight truncate leading-none">{displayName}</span><span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{item.time}</span></div>
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight leading-snug md:group-hover:text-rose-600 transition-colors line-clamp-3">{item.title}</h3>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-50 flex items-center justify-between">
                <div className={`px-2 py-0.5 rounded text-[8px] font-black text-white uppercase tracking-widest ${type === 'transfer' ? 'bg-rose-500' : 'bg-slate-900'}`}>{type === 'transfer' ? 'Transfer' : (item.type || 'Intel')}</div>
                <div className="flex items-center gap-1 text-[9px] font-black text-slate-300 uppercase tracking-widest">Read Feed <ArrowRight size={10} strokeWidth={3} /></div>
            </div>
        </div>
      );
  };

  const shouldShowUpsell = !user || !hasUserLeagues || majorGames.length === 0;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "XLEVEN",
    "url": "https://www.xleven.co",
    "description": lang === 'ko' 
      ? "가장 빠른 해외 축구 소식과 실시간 이적 루머 AI 분석" 
      : "Fastest international football news and real-time transfer rumors AI analysis",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.xleven.co/news?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title={seoTitle} description={seoDescription} jsonLd={jsonLd} />
      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} onNavigate={(item) => typeof item === 'string' ? onNavigateToPlayer(item) : window.navigateTo(`/feed/item/${item.id}`)} type="player" />

      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-50 px-4 h-14 flex items-center justify-between md:hidden">
        <XlevenLogo />
        <button onClick={() => setIsSearchOpen(true)} className="p-2 text-slate-900 md:hover:text-rose-500 transition-colors rounded-full md:hover:bg-gray-50"><Search size={20} /></button>
      </header>

      <div className="bg-white pt-4 pb-6">
        <div
          ref={gamesScrollRef}
          className="flex overflow-x-auto no-scrollbar gap-5 px-4 md:px-6 snap-x items-center cursor-grab active:cursor-grabbing select-none min-h-[104px]"
          onMouseDown={(e) => {
              if (loading) return;
              setIsDragging(false);
              const container = e.currentTarget;
              let isDown = true;
              let startX = e.pageX - container.offsetLeft;
              let scrollLeft = container.scrollLeft;

              const handleMouseMove = (e: MouseEvent) => {
                  if (!isDown) return;
                  e.preventDefault();
                  setIsDragging(true);
                  const x = e.pageX - container.offsetLeft;
                  const walk = (x - startX) * 2;
                  container.scrollLeft = scrollLeft - walk;
              };

              const handleMouseUp = () => {
                  isDown = false;
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
              };

              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
          }}
          onClickCapture={(e) => {
              if (isDragging) {
                  e.stopPropagation();
                  setIsDragging(false);
              }
          }}
        >
          {loading ? (
            [1, 2, 3].map(i => (
              <div key={i} className="shrink-0 w-44 md:w-48 h-14 md:h-16 bg-white rounded-full border border-gray-100 shadow-sm flex items-center justify-between px-5 my-5 animate-pulse">
                <Skeleton className="w-6 h-6 md:w-7 md:h-7" variant="circle" />
                <Skeleton className="w-12 h-4" />
                <Skeleton className="w-6 h-6 md:w-7 md:h-7" variant="circle" />
              </div>
            ))
          ) : majorGames.length > 0 ? majorGames.map(game => (
            <div key={game.id} onClick={() => window.navigateTo(`/game/${game.id}`)} className={`relative flex-shrink-0 w-44 md:w-48 h-14 md:h-16 bg-white rounded-full border transition-all cursor-pointer active:scale-95 flex items-center justify-between px-5 snap-center my-5 ${game.status === 'Live' ? 'border-rose-500 shadow-lg shadow-rose-50 ring-1 ring-rose-100' : 'border-gray-100 shadow-sm'}`}>
                <div className="absolute left-1/2 -top-2.5 -translate-x-1/2 z-10"><div className="px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-[0.1em] shadow-sm border border-white bg-slate-900 text-white truncate max-w-[100px]">{getLeagueName(parseInt(game.league), lang, game.leagueName)}</div></div>
                <div className="w-6 h-6 md:w-7 md:h-7 shrink-0"><img src={game.homeTeam.logo} alt="" className="w-full h-full object-contain" /></div>
                <div className="flex-1 flex flex-col items-center justify-center min-w-0"><span className={`text-base md:text-lg font-black tabular-nums leading-none tracking-tighter ${game.status === 'Live' ? 'text-rose-600' : 'text-slate-900'}`}>{game.status === 'Scheduled' ? game.time : game.score?.replace(':', ' : ')}</span></div>
                <div className="w-6 h-6 md:w-7 md:h-7 shrink-0"><img src={game.awayTeam.logo} alt="" className="w-full h-full object-contain" /></div>
                <div className="absolute left-1/2 -bottom-2.5 -translate-x-1/2 z-10">
                    <div className={`px-3 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm border border-white bg-white ${game.status === 'Live' ? 'text-rose-500' : 'text-slate-400'}`}>
                        {game.status === 'Live' ? <span className="animate-pulse">{game.currentMinute}'</span> : game.status === 'Finished' ? 'FT' : 'UP'}
                    </div>
                </div>
            </div>
          )) : (
            <div className="px-6 py-2 flex items-center gap-4 bg-slate-50 rounded-full border border-slate-100 ml-4">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] whitespace-nowrap">{t(lang, 'noMajorGamesToday')}</span>
                <button onClick={() => window.navigateTo(user ? '/games' : '/login')} className="flex items-center gap-1.5 px-4 py-1.5 bg-white border border-rose-100 rounded-full text-[9px] font-black text-rose-500 uppercase tracking-widest md:hover:bg-rose-500 md:hover:text-white transition-all shadow-sm active:scale-95 ring-1 ring-rose-50/50">
                  <Plus size={12} strokeWidth={4} />
                  <span className="hidden md:inline-block">{user ? t(lang, 'addFavoriteLeaguesButton') : t(lang, 'signIn')}</span>
                </button>
            </div>
          )}
        </div>
      </div>

      {/* 월드컵 배너 */}
      {wcBannerLoaded && wcBanner?.is_published && (
        <div className="px-4 pb-4">
          <button
            onClick={() => setIsWorldCupModalOpen(true)}
            className="w-full relative overflow-hidden rounded-3xl bg-slate-900 p-5 flex items-center justify-between gap-4 active:scale-[0.98] transition-transform"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-rose-900 opacity-80" />
            <div className="absolute right-0 top-0 bottom-0 w-40 bg-gradient-to-l from-rose-600/20 to-transparent" />
            <div className="relative z-10 flex items-center gap-4 min-w-0">
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
                <img src={wcBanner.image_url ?? '/assets/fiwc.png'} alt="FIFA World Cup 2026" className="w-8 h-8 object-contain" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-black text-rose-400 uppercase tracking-[0.15em]">FIFA World Cup 2026</span>
                </div>
                <p className="text-sm font-black text-white leading-tight">
                  {lang === 'ko' ? (wcBanner.title_ko ?? '16강 진출 경우의 수 보기') : (wcBanner.title_en ?? 'Group Stage Scenarios')}
                </p>
                <p className="text-[10px] text-white/50 mt-0.5 font-medium">
                  {lang === 'ko' ? (wcBanner.desc_ko ?? '각 팀의 16강 진출 가능성을 확인하세요') : (wcBanner.desc_en ?? "Check every team's qualification odds")}
                </p>
              </div>
            </div>
            <div className="relative z-10 shrink-0 flex items-center gap-1 bg-white/10 border border-white/10 rounded-full px-3 py-1.5">
              <span className="text-[10px] font-black text-white uppercase tracking-widest">{lang === 'ko' ? '보기' : 'View'}</span>
              <ChevronRight size={12} className="text-white" strokeWidth={3} />
            </div>
          </button>
        </div>
      )}

      <StandingsModal
        isOpen={isWorldCupModalOpen}
        onClose={() => setIsWorldCupModalOpen(false)}
        leagueId="1"
        leagueName="FIFA World Cup"
        leagueLogo="/assets/fiwc.png"
        season="2026"
        allowLeagueChange={false}
        initialSubTab={(wcBanner?.meta?.click_action ?? 'scenario') as any}
        initialKnockoutRound={wcBanner?.meta?.click_action === 'knockout' ? (wcBanner?.meta?.knockout_round as any) : undefined}
      />

      <div className="p-4 md:p-6 lg:p-8 space-y-12">
        
        <React.Suspense fallback={<div className="h-48 bg-gray-50 animate-pulse rounded-3xl" />}>
          <HighlightSection lang={lang} />
        </React.Suspense>

        <section>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-slate-900 text-lg font-black tracking-tight uppercase border-l-4 border-rose-500 pl-3">{t(lang, 'recommendedForYou')}</h2>
                <button onClick={() => window.navigateTo('/feed')} className="text-[10px] font-black text-rose-500 uppercase tracking-widest md:hover:underline flex items-center gap-1">{t(lang, 'viewAll')}  <ChevronRight size={12} /></button>
            </div>
            <div
                ref={newsScrollRef}
                className="flex overflow-x-auto no-scrollbar gap-4 pb-4 snap-x cursor-grab active:cursor-grabbing select-none"
                onMouseDown={loading ? undefined : handleMouseDown}
                onClickCapture={loading ? undefined : handleClickCapture}
            >
                {loading ? (
                    [1, 2, 3].map(i => (
                        <div key={i} className="flex-shrink-0 w-[280px] md:w-[320px] bg-white rounded-2xl border border-gray-100 p-4 shadow-sm animate-pulse">
                            <div className="flex justify-between items-start gap-3">
                                <div className="flex-1 flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <Skeleton className="w-12 h-4 rounded" />
                                        <Skeleton className="w-20 h-4 rounded" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Skeleton className="w-full h-5 rounded" />
                                        <Skeleton className="w-2/3 h-5 rounded" />
                                    </div>
                                    <Skeleton className="w-12 h-3 mt-2 rounded" />
                                </div>
                                <Skeleton className="w-20 h-20 rounded-xl shrink-0" />
                            </div>
                        </div>
                    ))
                ) : pickedNews.length > 0 ? (
                    pickedNews.map((item) => <HomeNewsCard key={item.id} item={item} type='general' />)
                ) : (
                    <div className="py-20 text-center w-full border-2 border-dashed border-gray-100 rounded-3xl text-slate-300 font-black text-xs uppercase tracking-widest">
                        {t(lang, 'scanningSectorIntel')}
                    </div>
                )}
            </div>
        </section>

        <React.Suspense fallback={null}>
          <MatchAnalysisSection 
            onMouseDown={handleMouseDown}
            onClickCapture={handleClickCapture}
          />
        </React.Suspense>

        {magazineNews.length > 0 && (
          <React.Suspense fallback={null}>
            <MagazineSection 
              news={magazineNews} 
              lang={lang} 
              onMouseDown={handleMouseDown}
              onClickCapture={handleClickCapture}
            />
          </React.Suspense>
        )}


        {/* <React.Suspense fallback={<div className="h-64 bg-slate-900/50 animate-pulse rounded-3xl" />}>
          <WebtoonSection 
            news={webtoonNews} 
            lang={lang} 
            onMouseDown={handleMouseDown}
            onClickCapture={handleClickCapture}
          />
        </React.Suspense> */}

        <React.Suspense fallback={null}>
          <TeamRankingSection 
            lang={lang as any}
            onMouseDown={handleMouseDown}
            onClickCapture={handleClickCapture}
          />
        </React.Suspense>

        <section>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-slate-900 text-lg font-black tracking-tight uppercase border-l-4 border-amber-500 pl-3">{t(lang, 'latestMovements')}</h2>
                <button onClick={() => window.navigateTo('/market/latest_movements')} className="text-[10px] font-black text-amber-600 uppercase tracking-widest md:hover:underline flex items-center gap-1">{t(lang, 'viewAll')} <ChevronRight size={12} /></button>
            </div>
            <React.Suspense fallback={<div className="h-32 bg-gray-50 animate-pulse rounded-2xl" />}>
              <LatestMovements players={trendingPlayers} onNavigate={onNavigateToPlayer} />
            </React.Suspense>
        </section>

        {transferNews.length > 0 && (
            <section>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-slate-900 text-lg font-black tracking-tight uppercase border-l-4 border-slate-900 pl-3">{t(lang, 'latestTransferNews')}</h2>
                    <button onClick={() => window.navigateTo('/feed')} className="text-[10px] font-black text-slate-400 uppercase tracking-widest md:hover:underline flex items-center gap-1">{t(lang, 'viewAll')} <ChevronRight size={12} /></button>
                </div>
                <div
                    ref={transferNewsScrollRef}
                    className="flex overflow-x-auto no-scrollbar gap-4 pb-4 snap-x cursor-grab active:cursor-grabbing select-none"
                    onMouseDown={handleMouseDown}
                    onClickCapture={handleClickCapture}
                >
                    <React.Suspense fallback={<div className="w-[300px] h-[200px] bg-gray-50 animate-pulse rounded-[28px]" />}>
                      {transferNews.map((item) => <HomeNewsCard key={item.id} item={item} type='transfer' />)}
                    </React.Suspense>
                </div>
            </section>
        )}

      </div>
      <div className="md:hidden">
        <AppFooter lang={lang as any} />
      </div>
    </div>
  );
};
